import moment from "moment-timezone";
import RRule, { RRuleSet, rrulestr } from "rrule";

export const WEEKDAY_NAME = [
  "Sundays",
  "Mondays",
  "Tuesdays",
  "Wednesdays",
  "Thursdays",
  "Fridays",
  "Saturdays",
];

export interface IScheduleTime {
  /** day of the week, starting with sunday == 0 */
  weekday: number;
  /** hour and minute as a 4-digit number (HHMM) */
  time: TimeInt;
  /** olson timezone where this time is happening */
  tzid: string;
}

export type TimeInt = number & { _tag: "__TIME_INT__" };
export type RRuleStr = string & { _tag: "__RRULE_STR__" };

/** converts a time integer into an hour/minute pair */
export const decodeTimeInt = (t: TimeInt): [number, number] => {
  let hour = Math.floor(t / 100);
  let minute = Math.floor(t) % 100;

  // add an hour if minutes part > 60
  hour = (hour + Math.floor(minute / 60)) % 24;
  minute = minute % 60;

  return [hour, minute];
};

/** converts an hour/minute pair into a time integer */
export const encodeTimeInt = (hour: number, minute: number): TimeInt => {
  // add an hour if minutes part > 60
  hour = (hour + Math.floor(minute / 60)) % 24;
  minute = minute % 60;

  return (hour * 100 + minute) as TimeInt;
};

/**
 * finds all of the times when the schedule fires between the start and end time
 * @param entry the schedule entry to check
 * @param start window start in in ms since epoch
 * @param start window end in ms since epoch
 */
export const findTimesBetween = (
  entry: IScheduleTime,
  start: number,
  end: number
) => {
  const times: number[] = [];
  if (start >= end) {
    return times;
  }
  while (true) {
    const next = findNextTime(entry, start);

    if (next > end) {
      break;
    }

    times.push(next);
    start = next;
  }
  return times;
};

/**
 * finds the next time when this schedule entry applies
 *
 * @param entry the schedule entry to check
 * @param now time in ms from epoch when the search begins
 */
export const findNextTime = (
  { tzid, weekday, time }: IScheduleTime,
  now?: number
): number => {
  const [hour, minute] = decodeTimeInt(time);

  const t = moment.tz(now, tzid);
  const nextTime = t
    .clone()
    .hour(hour)
    .minute(minute)
    .second(0)
    .millisecond(0);

  // find the day with the next instance...
  const weekdayOffset = (weekday - nextTime.weekday() + 7) % 7;
  nextTime.add(weekdayOffset, "days");

  // if there was an instance earlier today...
  if (nextTime.isSameOrBefore(t)) {
    nextTime.add(7, "days");
  }

  return nextTime.toDate().getTime();
};

/**
 * Converts a list of schedule entries to an ICAL RRULE string
 *
 * @param schedule list of schedule entries
 * @param now if provided, overrides the current time dtstart calculation
 */
export const scheduleToRRule = (
  schedule: IScheduleTime[],
  now?: number
): RRuleStr => {
  const set = new RRuleSet();

  for (const entry of schedule) {
    const [hour, minute] = decodeTimeInt(entry.time);

    const dtstartEpoch = findNextTime(entry, now);
    const dtstartLocal = moment(dtstartEpoch).tz(entry.tzid);

    // Annoyingly, this library requires passing a UTC date with the same hour/minute
    // as the local time. Construct one here.
    const dtstart = new Date(
      Date.UTC(
        dtstartLocal.year(),
        dtstartLocal.month(),
        dtstartLocal.date(),
        dtstartLocal.hours(),
        dtstartLocal.minutes()
      )
    );

    const rrWeekday = (entry.weekday - 1 + 7) % 7; // RRule starts week Monday not Sunday

    const rule = new RRule({
      dtstart,
      tzid: entry.tzid,
      freq: RRule.WEEKLY,
      byhour: [hour],
      byminute: [minute],
      bysecond: [0],
      byweekday: [rrWeekday],
    });

    set.rrule(rule);
  }

  return set.toString() as RRuleStr;
};

/**
 * Converts an ICAL RRULE string into a list of schedule entries
 */
export const rruleToSchedule = (rrStr: RRuleStr): IScheduleTime[] => {
  if (!rrStr) {
    return [];
  }

  const parsed = rrulestr(rrStr);

  let rules: RRule[] = [];
  if (parsed instanceof RRuleSet) {
    rules = [...parsed._rrule];
  } else {
    rules = [parsed];
  }

  const schedule: IScheduleTime[] = [];

  for (const rule of rules) {
    const { freq, byhour, byminute, byweekday, tzid } = rule.options;

    // only weekly recurrences supported
    if (freq !== RRule.WEEKLY) {
      continue;
    }
    if (
      byhour.length === 0 ||
      byminute.length === 0 ||
      byweekday.length === 0 ||
      !tzid
    ) {
      continue;
    }

    const hour = byhour[0];
    const minute = byminute[0];

    const rrWeekday = byweekday[0];
    const weekday = (rrWeekday + 1) % 7; // JS starts week Sunday not Monday

    const time = encodeTimeInt(hour, minute);

    schedule.push({
      weekday,
      time,
      tzid,
    });
  }

  return schedule;
};

export const dummyTime = (
  tzid: string,
  lastTime?: IScheduleTime,
  interval: number = 30
): IScheduleTime => {
  let when = lastTime
    ? moment.tz(findNextTime(lastTime), lastTime.tzid)
    : moment.tz(tzid).local();

  const minute = Math.floor(when.minute() / interval) * interval;
  when = when
    .minute(0)
    .second(0)
    .add(minute + interval, "minutes");

  const weekday = when.weekday();
  const time = encodeTimeInt(when.hours(), when.minutes());

  return { weekday, time, tzid };
};

export const formatTime = (time: number) => {
  return moment()
    .hour(Math.floor(time / 100))
    .minute(Math.floor(time) % 100)
    .format("h:mm A");
};



// WEBPACK FOOTER //
// ./src/Schedule.ts